import { AlertTriangleIcon, AwardIcon, DownloadIcon } from 'lucide-react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router'

import { Alert, AlertDescription, AlertTitle } from '@/components/elements/alert'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { TextLink } from '@/components/TextLink'
import { formatDate } from '@/utils/formatters'

function CertificationsSection({ recentCertifications, expiringCertifications }) {
  const { entityId } = useParams()
  const hasRecent = recentCertifications.length > 0
  const hasExpiring = expiringCertifications.length > 0

  if (!hasRecent && !hasExpiring) {
    return (
      <section>
        <h2 className="mb-4 flex items-center gap-2 uppercase">
          <AwardIcon className="h-5 w-5" />
          Certifications
        </h2>
        <Card>
          <CardContent className="py-8 text-center">
            <AwardIcon className="text-muted-foreground/50 mx-auto mb-3 h-10 w-10" />
            <h3 className="mb-1 text-base font-semibold">No Certifications Yet</h3>
            <p className="text-muted-foreground text-sm">
              Earned certifications and upcoming expirations will appear here.
            </p>
          </CardContent>
        </Card>
      </section>
    )
  }

  return (
    <section>
      <h2 className="mb-4 flex items-center gap-2 uppercase">
        <AwardIcon className="h-5 w-5" />
        Certifications
      </h2>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Certifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AwardIcon className="h-5 w-5 text-green-600" />
                Recent Certifications
              </CardTitle>
              <TextLink to={`/accounts/${entityId}/documents`}>View All</TextLink>
            </div>
          </CardHeader>
          <CardContent>
            {hasRecent ? (
              <ul className="space-y-3">
                {recentCertifications.map((cert) => (
                  <li
                    key={cert.id}
                    className="flex items-center justify-between border-b pb-2 last:border-b-0"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{cert.certificateName}</span>
                      <span className="text-muted-foreground text-sm">
                        {cert.certificateNumber && `#${cert.certificateNumber} - `}
                        Issued {formatDate(cert.dateIssued)}
                      </span>
                    </div>
                    {cert.downloadUrl && (
                      <Button variant="ghost" size="icon" asChild>
                        <a
                          href={cert.downloadUrl}
                          download
                          title="Download Certificate"
                          aria-label={`Download certificate ${cert.certificateName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DownloadIcon className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">No recent certifications</p>
            )}
          </CardContent>
        </Card>

        {/* Expiring Certifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangleIcon className="h-5 w-5 text-amber-500" />
                Expiring Soon
              </CardTitle>
              {hasExpiring && <TextLink to={`/accounts/${entityId}/documents`}>View All</TextLink>}
            </div>
          </CardHeader>
          <CardContent>
            {hasExpiring ? (
              <ul className="space-y-3">
                {expiringCertifications.map((cert) => (
                  <li key={cert.id}>
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertTriangleIcon className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-amber-800">{cert.certificateName}</AlertTitle>
                      <AlertDescription className="text-amber-700">
                        Expires {formatDate(cert.expiresAt)}
                      </AlertDescription>
                    </Alert>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">No certifications expiring soon</p>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  )
}

CertificationsSection.propTypes = {
  recentCertifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      certificateName: PropTypes.string.isRequired,
      certificateNumber: PropTypes.string,
      dateIssued: PropTypes.string,
      downloadUrl: PropTypes.string,
    }),
  ).isRequired,
  expiringCertifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      certificateName: PropTypes.string.isRequired,
      certificateNumber: PropTypes.string,
      dateIssued: PropTypes.string,
      expiresAt: PropTypes.string,
    }),
  ).isRequired,
}

export default CertificationsSection
